<template>
  <div v-if="!isLoading" class="mb-5">
    <flickity
      v-if="promotionList.length > 1"
      ref="flickity"
      :options="sliderConfig"
      class="slider-card">
      <div
        class="carousel-cell promotion-card-wrapper pb-4"
        v-for="(promotion, promotionIndex) in promotionList">
        <PromotionCard
          @select="selectPromotion"
          @apply="applyPromotion"
          :promotion="promotion"
          :promotionIndex="promotionIndex" />
      </div>
    </flickity>
    <PromotionCard
      v-if="promotionList.length === 1"
      @select="selectPromotion"
      @apply="applyPromotion"
      :promotion="promotionList[0]"
      :promotionIndex="0" />
    <b-modal
      size="sm"
      hide-header
      hide-footer
      body-class="p-0"
      v-model="showDetailPopup">
      <PromotionCard
        v-if="$isNotEmptyV2(promotionDetailSelected)"
        :showDetail="true"
        @apply="applyPromotion"
        @closeDetail="closeDetailPromotion"
        :promotion="promotionDetailSelected" />
    </b-modal>
  </div>
</template>

<script>
import { get } from '@/services/index.js';
import { mapGetters, mapMutations } from 'vuex';
import Flickity from 'vue-flickity';
import PromotionCard from './PromotionCard.vue';

export default {
  components: {
    Flickity,
    PromotionCard,
  },
  data() {
    return {
      isLoading: true,
      sliderConfig: {
        initialIndex: 0,
        autoPlay: 5000,
        prevNextButtons: false,
        pageDots: true,
        adaptiveHeight: false,
        draggable: false,
        wrapAround: false,
        contain: true,
      },
      sliderElement: null,
      promotionList: [],
      showDetailPopup: false,
      promotionDetailSelected: {},
    };
  },
  computed: {
    ...mapGetters(['getMerchant', 'getBeFilter', 'getSelectedPromotion']),
    isUseSlider() {
      return this.promotionList.length > 1;
    },
  },
  created() {
    if (window.innerWidth < 577) {
      this.sliderConfig.pageDots = false;
      this.sliderConfig.draggable = true;
    }
  },
  async mounted() {
    await this.loadPromotionList();
    if (this.isUseSlider) this.initSlider();
  },
  methods: {
    ...mapMutations(['setSelectedPromotion', 'resetSelectedPromotion']),
    initSlider() {
      this.$nextTick(() => {
        this.sliderElement = this.$refs.flickity ? this.$refs.flickity : null;
      });
    },
    getPromotionApiParams() {
      let { check_in, check_out } = this.getBeFilter.date;
      return {
        merchant_id: this.getMerchant.id,
        check_in_date: this.$formatDateApi(check_in),
        check_out_date: this.$formatDateApi(check_out),
      };
    },
    validatePromotionFormula(formulaList) {
      const isListArray = Array.isArray(formulaList);
      if (!isListArray) return false;

      const selectedFormula = formulaList[0];
      const isFormulaValid = selectedFormula.calculate_by === 'DECREASE';
      return isFormulaValid;
    },
    generateDiscountAmount(formulaList) {
      const isFormulaValid = this.validatePromotionFormula(formulaList);
      if (!isFormulaValid) return null;

      const formula = formulaList[0];
      const { value, value_type: valueType } = formula;
      const valueString =
        valueType === 'PERCENT'
          ? `${value}%`
          : this.$formatCurrencyCompact(value);

      return valueString;
    },
    async generateBiggestFormula(formulaList) {
      const isFormulaSub = formulaList.some((formula) =>
        this.$isNotEmptyV2(formula.formula_list)
      );
      let validFormulaType = null;
      if (isFormulaSub) {
        validFormulaType = formulaList.filter((formula) =>
          this.validatePromotionFormula(formula.formula_list)
        );
        if (this.$isNotEmptyV2(validFormulaType))
          validFormulaType = validFormulaType.map((formula) => {
            formula = formula.formula_list[0];
            return formula;
          });
      } else {
        validFormulaType = this.validatePromotionFormula(formulaList)
          ? formulaList
          : null;
      }
      if (this.$isEmpty(validFormulaType)) return null;

      const validateFormulaList = (valueType) =>
        validFormulaType.every((formula) => formula.value_type === valueType);

      const isAllPercent = validateFormulaList('PERCENT');
      const isAllAmount = validateFormulaList('AMOUNT');
      const isCombine = !(isAllPercent || isAllAmount);

      if (isCombine) return validFormulaType[0];

      const sortedList = validFormulaType.sort((a, b) => b.value - a.value);
      return [sortedList[0]];
    },
    async parsingDiscountAmount(promotionList) {
      try {
        const duplicatePromotions = this.$deepCopy(promotionList);
        let updatedPromotions = [];

        const setUpdatedPromotions = (formulaList, originalData) => {
          const discountAmount = this.generateDiscountAmount(formulaList);
          if (this.$isNotEmptyV2(discountAmount)) {
            let updatedPromotion = Object.assign(originalData, {
              formula_text: discountAmount,
            });
            updatedPromotions.push(updatedPromotion);
          }
          return Promise.resolve();
        };

        for (const promotion of duplicatePromotions) {
          const { formula } = promotion;

          const isMultipleFormula = Array.isArray(formula);
          if (isMultipleFormula) {
            let biggestFormula = await this.generateBiggestFormula(formula);
            if (biggestFormula !== null)
              await setUpdatedPromotions(biggestFormula, promotion);
          } else {
            await setUpdatedPromotions(formula.formula_list, promotion);
          }
        }

        return updatedPromotions;
      } catch (error) {
        throw error;
      }
    },
    generatePromotionRule(promotionList) {
      return promotionList.map((promotion) => {
        const { min_night, promotion_type, cut_off, formula } = promotion;

        promotion.rule_list = [];

        if (min_night > 1) {
          const minNightRule = `Minimum ${min_night}-night stay required.`;
          promotion.rule_list.push(minNightRule);
        }

        const isMultiNightWithCutOff =
          promotion_type === 'MULTI_NIGHT' && cut_off > 0;
        const isEarlyBirdWithCutOff =
          promotion_type === 'EARLY_BIRD' && formula.cut_off > 0;
        if (isMultiNightWithCutOff || isEarlyBirdWithCutOff) {
          const cutOff =
            promotion_type === 'MULTI_NIGHT' ? cut_off : formula.cut_off;
          const minNightRule = `Book ${cutOff}+ days before check-in.`;
          promotion.rule_list.push(minNightRule);
        }
        return promotion;
      });
    },
    async loadPromotionList() {
      try {
        this.isLoading = true;
        this.promotionList = [];

        const apiParams = this.getPromotionApiParams();
        const apiresponse = await get(
          'CORE_HOTEL',
          'search-room/promotion',
          apiParams
        );
        if (!apiresponse.data.success)
          throw new Error(apiresponse.data.message);

        this.promotionList = await this.parsingDiscountAmount(
          apiresponse.data.data
        );
        this.promotionList = this.sortingPromotionList(this.promotionList);
        this.promotionList = this.generatePromotionRule(this.promotionList);
        return Promise.resolve();
      } catch (error) {
        console.log(
          'ERROR in loadPromotionList() (PotentialPromotion.vue) : ',
          error
        );
      } finally {
        this.isLoading = false;
      }
    },
    sortingPromotionList(promotionList) {
      if (this.$isEmpty(this.getSelectedPromotion)) return promotionList;

      const promotionIndex = promotionList.findIndex(
        (promotion) => promotion.id === this.getSelectedPromotion.id
      );
      if (promotionIndex === -1) {
        this.resetSelectedPromotion();
        return promotionList;
      }

      let newPromotionList = promotionList;
      const [activePromotion] = newPromotionList.splice(promotionIndex, 1);
      newPromotionList.unshift(activePromotion);
      return newPromotionList;
    },
    selectPromotion(index) {
      if (this.isUseSlider) {
        let activeSlide = this.sliderElement.selectedIndex();
        if (index != activeSlide) this.sliderElement.select(index);
        else this.openDetailPromotion(index);
      } else {
        this.openDetailPromotion(index);
      }
    },
    applyPromotion(index) {
      const isFromDetail = index === -2;
      const selectedPromotion = isFromDetail
        ? this.promotionDetailSelected
        : this.promotionList[index];
      if (isFromDetail || !this.isUseSlider) {
        this.setSelectedPromotion(selectedPromotion);
      } else {
        let activeSlide = this.sliderElement.selectedIndex();
        if (index === activeSlide) this.setSelectedPromotion(selectedPromotion);
        else this.selectPromotion(index);
      }
    },

    // DETAIL
    openDetailPromotion(index) {
      const selectedPromotion = this.promotionList[index];
      if (this.$isEmpty(selectedPromotion)) return;

      this.promotionDetailSelected = selectedPromotion;
      this.toggleDetailPopup(true);
    },
    closeDetailPromotion() {
      this.promotionDetailSelected = {};
      this.toggleDetailPopup();
    },
    toggleDetailPopup(show = false) {
      this.showDetailPopup = show;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/hotel/be/PotentialPromotion.vue